import { useEffect, useRef, useState } from 'react';

import { safeResizeObserver } from '@polyai/common/utils/safeResizeObserver';

type SizeAttributes =
  | 'offsetWidth'
  | 'offsetHeight'
  | 'clientWidth'
  | 'clientHeight'
  | 'scrollWidth'
  | 'scrollHeight';

function useIsOverflowing<T extends HTMLElement = HTMLDivElement>(
  targetAttribute: SizeAttributes = 'scrollWidth',
  maxWidth?: number,
  contentDependency?: unknown,
) {
  const targetRef = useRef<T>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
  useEffect(() => {
    const checkOverflow = () => {
      if (targetRef.current) {
        const el = targetRef.current;
        const isVertical =
          targetAttribute === 'scrollHeight' ||
          targetAttribute === 'offsetHeight' ||
          targetAttribute === 'clientHeight';
        const limit =
          maxWidth ?? (isVertical ? el.clientHeight : el.clientWidth);
        setIsOverflow(el[targetAttribute] > limit);
      } else {
        setIsOverflow(false);
      }
    };

    const targetObserver = safeResizeObserver(checkOverflow);

    if (targetRef.current) {
      targetObserver.observe(targetRef.current);
    }

    checkOverflow();

    return () => {
      targetObserver.disconnect();
    };
  }, [maxWidth, targetAttribute, contentDependency]);

  return { isOverflow, targetRef };
}

export default useIsOverflowing;
